import moment from "moment";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLazyViewSolarEstimationUpdateHistoryQuery } from "../../../../../redux/api/solar-estimation/solarEstimationAPI";
import { selectAuth } from "../../../../../redux/features/auth/auth-slice";
import { ViewSolarEstimationUpdateHistoryResponseDTO } from "../../../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-update-history-response-dto";
import { EstimationStatus } from "../../../../../shared/oversight-core/enums/estimation-status";
import { ESolarSolutionType } from "../../../../../shared/oversight-core/enums/sola-solution-type";
import { EViewSolarEstimationSolarProductCategory } from "../../../../../shared/oversight-core/enums/view-solar-estimation-solar-product-category";
import { ISolarEstimationUpdateHistoryView } from "../../../../../shared/oversight-core/interfaces/solar-estimation-update-history-view";
import AppBanner from "../../../../../shared/oversight-core/ui-elements/app-banner/app-banner";
import { MtIcon } from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import SpinnerModal from "../../../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  getStatusColor,
  getStatusNameAdmin,
  getStatusNameUser,
} from "../../../../../shared/oversight-core/utils/estimation-status";
import { formatWithCommas } from "../../../../../shared/oversight-core/utils/format-with-commas";
import Helper from "../../../../../shared/oversight-core/utils/helpers";
import { convertUTCToLocal } from "../../../../../shared/oversight-core/utils/time-utils";
import SolarHistoryCard from "../solar-history-card/solar-history-card";

interface IProps extends ModelContainerProps {
  show: boolean;
  estimationId: string;
  estimatorToken?: string;
  user?: string;
  currency: string;
  estimationNo: number;
  isUserView?: boolean;
}

type HistoryVersionRowProps = {
  history: ISolarEstimationUpdateHistoryView;
  currency: string;
  findIcon: (category: EViewSolarEstimationSolarProductCategory) => MtIcon;
  findName: (version: number, id: string) => string;
};

const UpdateHistoryModal = (props: IProps) => {
  const {
    show,
    estimationId,
    estimatorToken,
    user,
    currency,
    estimationNo,
    isUserView = true,
    ...rest
  } = props;

  const auth = useSelector(selectAuth);

  const [updateHistory, setUpdateHistory] = useState<
    ISolarEstimationUpdateHistoryView[]
  >([]);

  const [triggerViewSolarEstimationUpdateHistory, { isFetching }] =
    useLazyViewSolarEstimationUpdateHistoryQuery();

  useEffect(() => {
    if (show) {
      triggerViewSolarEstimationUpdateHistory(
        auth.token
          ? {
              estimationId,
            }
          : { estimatorToken },
      )
        .unwrap()
        .then((res: ViewSolarEstimationUpdateHistoryResponseDTO) => {
          setUpdateHistory(res.history);
        })
        .catch(() => {
          setUpdateHistory([]);
        });
    }
  }, [show]);

  const findIcon = (category: EViewSolarEstimationSolarProductCategory) => {
    switch (category) {
      case EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS:
        return "google_tv_remote";
      case EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS:
        return "google_tv_remote";
      case EViewSolarEstimationSolarProductCategory.BATTERIES:
        return "battery_horiz_000";
      case EViewSolarEstimationSolarProductCategory.PANELS:
        return "solar_power";
      case EViewSolarEstimationSolarProductCategory.WATER_HEATERS:
        return "propane";
      default:
        return "tablet_mac";
    }
  };

  const findName = (version: number, id: string): string => {
    const history = updateHistory.find((i) => i.version === version);

    const product = history?.products.find((p) => p.id === id);

    return product?.name ?? "";
  };

  const findUserSelectedProduct = (
    version: number,
    category: EViewSolarEstimationSolarProductCategory,
    isBefore: boolean,
  ): {
    icon: MtIcon;
    name: string;
    count: number;
    id: string;
    isSelected: boolean;
  } => {
    const history = updateHistory.find((i) => i.version === version);

    const userSelectedProduct = (
      isBefore ? history?.before : history?.after
    )?.products.find((f) => f.category === category);

    return {
      icon: findIcon(category),
      name: userSelectedProduct
        ? findName(version, userSelectedProduct.id)
        : "",
      count: userSelectedProduct ? userSelectedProduct.count : 0,
      id: userSelectedProduct ? userSelectedProduct.id : "",
      isSelected:
        category === EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS
          ? !!userSelectedProduct?.isSelected
          : true,
    };
  };

  const solarCategories = [
    EViewSolarEstimationSolarProductCategory.INVERTERS,
    EViewSolarEstimationSolarProductCategory.BATTERIES,
    EViewSolarEstimationSolarProductCategory.PANELS,
    EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS,
  ];

  const waterCategories = [
    EViewSolarEstimationSolarProductCategory.WATER_HEATERS,
    EViewSolarEstimationSolarProductCategory.WATER_HEATER_CONTROLLERS,
  ];

  const CurrentVersionRow = ({
    history,
    currency,
    findIcon,
    findName,
  }: HistoryVersionRowProps) => (
    <Row className="bg-background-10 px-1 py-3 rounded-2 mx-0">
      <Col>
        <Row className="align-items-center">
          <Col>
            <Row>
              <Col className="title-4 font-weight-500 font-size-20">
                Current Version
              </Col>
            </Row>
            <Row>
              <Col className="text-secondary font-weight-400 font-size-10">
                {convertUTCToLocal(
                  history?.after?.lastUpdated,
                  "hh:mm a dd/MM/yyyy",
                )}
              </Col>
            </Row>
          </Col>
          <Col className="col-auto">
            <div
              className={`${getStatusColor(
                history?.status as EstimationStatus,
              )} py-1 px-3 rounded-5 font-weight-400 font-size-12`}
            >
              {getStatusNameAdmin(history?.status as EstimationStatus)} |
              by&nbsp;
              {history?.status === EstimationStatus.PURCHASED
                ? `${history?.actionUser.firstName} ${history?.actionUser.lastName}`
                : `Admin`}
            </div>
          </Col>
        </Row>
        {history?.adminComment && (
          <Row className="my-3 mx-0">
            <Col>
              <AppBanner
                content={
                  <Row>
                    <Col className="font-weight-400 font-size-12 text-color-7">
                      <span className="text-color-8">Admin Note:</span>
                      &nbsp;
                      {history?.adminComment}
                    </Col>
                  </Row>
                }
                bannerVariant="lightBlueTwo"
                className="text-start p-3 rounded-2"
              />
            </Col>
          </Row>
        )}
        {history?.estimationType !==
          ESolarSolutionType.WATER_HEATING_SYSTEM && (
          <>
            <Row className="mt-3">
              <Col className="text-secondary font-weight-500 font-size-13">
                Solar Power Equipments
              </Col>
            </Row>
            <Row>
              <Col>
                {history?.after?.products.length >
                history?.before.products.length
                  ? history?.after?.products
                      .filter((i) => solarCategories.includes(i.category))
                      .map((i) => {
                        if (!i.id) return null;
                        const userProduct = findUserSelectedProduct(
                          history.version,
                          i.category,
                          true,
                        );
                        if (
                          i.category ===
                            EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS &&
                          !i.isSelected &&
                          !userProduct.isSelected
                        ) {
                          return null;
                        }
                        return (
                          <Row
                            key={i.id}
                            className="gx-3 my-2 align-items-center"
                          >
                            <Col>
                              {userProduct.id && userProduct.isSelected ? (
                                <SolarHistoryCard
                                  icon={userProduct.icon}
                                  name={userProduct.name}
                                  count={userProduct.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-11"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                            <Col>
                              {i.id &&
                              (i.category !==
                                EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS ||
                                i.isSelected) ? (
                                <SolarHistoryCard
                                  icon={findIcon(i.category)}
                                  name={findName(history.version, i.id)}
                                  count={i.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-12"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                          </Row>
                        );
                      })
                  : history?.before?.products
                      .filter((i) => solarCategories.includes(i.category))
                      .map((i) => {
                        if (!i.id) return null;
                        const userProduct = findUserSelectedProduct(
                          history.version,
                          i.category,
                          false,
                        );
                        if (
                          i.category ===
                            EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS &&
                          !i.isSelected &&
                          !userProduct.isSelected
                        ) {
                          return null;
                        }
                        return (
                          <Row key={i.id} className="gx-3 my-2">
                            <Col>
                              {i.id &&
                              (i.category !==
                                EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS ||
                                i.isSelected) ? (
                                <SolarHistoryCard
                                  icon={findIcon(i.category)}
                                  name={findName(history.version, i.id)}
                                  count={i.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-12"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                            <Col>
                              {userProduct.id && userProduct.isSelected ? (
                                <SolarHistoryCard
                                  icon={userProduct.icon}
                                  name={userProduct.name}
                                  count={userProduct.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-11"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                          </Row>
                        );
                      })}
              </Col>
            </Row>
          </>
        )}
        {(history?.estimationType === ESolarSolutionType.BOTH ||
          history?.estimationType ===
            ESolarSolutionType.WATER_HEATING_SYSTEM) && (
          <>
            <Row className="mt-2">
              <Col className="text-secondary font-weight-500 font-size-13">
                Water Heater Equipments
              </Col>
            </Row>
            <Row>
              <Col>
                {history?.after?.products.length >
                history?.before.products.length
                  ? history?.after?.products
                      .filter((i) => waterCategories.includes(i.category))
                      .map((i) => {
                        if (!i.id) return null;
                        const userProduct = findUserSelectedProduct(
                          history.version,
                          i.category,
                          true,
                        );
                        return (
                          <Row key={i.id} className="gx-3 my-2">
                            <Col>
                              {userProduct.id && userProduct.isSelected ? (
                                <SolarHistoryCard
                                  icon={userProduct.icon}
                                  name={userProduct.name}
                                  count={userProduct.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-11"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                            <Col>
                              {i.id ? (
                                <SolarHistoryCard
                                  icon={findIcon(i.category)}
                                  name={findName(history.version, i.id)}
                                  count={i.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-12"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                          </Row>
                        );
                      })
                  : history?.before?.products
                      .filter((i) => waterCategories.includes(i.category))
                      .map((i) => {
                        if (!i.id) return null;
                        const userProduct = findUserSelectedProduct(
                          history.version,
                          i.category,
                          false,
                        );
                        return (
                          <Row key={i.id} className="gx-3 my-2">
                            <Col>
                              {i.id ? (
                                <SolarHistoryCard
                                  icon={findIcon(i.category)}
                                  name={findName(history.version, i.id)}
                                  count={i.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-12"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                            <Col>
                              {userProduct.id && userProduct.isSelected ? (
                                <SolarHistoryCard
                                  icon={userProduct.icon}
                                  name={userProduct.name}
                                  count={userProduct.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-11"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                          </Row>
                        );
                      })}
              </Col>
            </Row>
          </>
        )}
        <Row className="mt-3">
          <Col>
            {(!!history?.before?.equipmentCost ||
              !!history?.after?.equipmentCost) && (
              <Row className="row-cols-auto align-items-center justify-content-between my-1">
                <Col className="text-label font-weight-500 font-size-16">
                  {currency}&nbsp;
                  {formatWithCommas(
                    Number(Helper.roundTo2(history?.before?.equipmentCost)),
                  )}
                </Col>
                <Col className="text-advance-description font-weight-400 font-size-14">
                  Components Cost
                </Col>
                <Col className="text-label font-weight-500 font-size-16">
                  {currency}&nbsp;
                  {formatWithCommas(
                    Number(Helper.roundTo2(history?.after?.equipmentCost)),
                  )}
                </Col>
              </Row>
            )}
            {(!!history?.before?.totalLaborCost ||
              !!history?.after?.totalLaborCost) &&
              history.installationCostRequested && (
                <Row className="row-cols-auto align-items-center justify-content-between my-1">
                  <Col className="text-label font-weight-500 font-size-16">
                    {currency}&nbsp;
                    {formatWithCommas(
                      Number(Helper.roundTo2(history?.before?.totalLaborCost)),
                    )}
                  </Col>
                  <Col className="text-advance-description font-weight-400 font-size-14">
                    {history?.status === EstimationStatus.APPROVED ||
                    history?.status === EstimationStatus.APPROVED_WITH_CHANGES
                      ? `Installation Cost`
                      : `Approximate Installation Cost`}
                  </Col>
                  <Col className="text-label font-weight-500 font-size-16">
                    {currency}&nbsp;
                    {formatWithCommas(
                      Number(Helper.roundTo2(history?.after?.totalLaborCost)),
                    )}
                  </Col>
                </Row>
              )}
            {(!!history?.before?.totalAccessoriesCost ||
              !!history?.after?.totalAccessoriesCost) &&
              history.installationCostRequested && (
                <Row className="row-cols-auto align-items-center justify-content-between my-1">
                  <Col className="text-label font-weight-500 font-size-16">
                    {currency}&nbsp;
                    {formatWithCommas(
                      Number(
                        Helper.roundTo2(history?.before?.totalAccessoriesCost),
                      ),
                    )}
                  </Col>
                  <Col className="text-advance-description font-weight-400 font-size-14">
                    {history?.status === EstimationStatus.APPROVED ||
                    history?.status === EstimationStatus.APPROVED_WITH_CHANGES
                      ? `Accessories Cost`
                      : `Approximate Accessories Cost`}
                  </Col>
                  <Col className="text-label font-weight-500 font-size-16">
                    {currency}&nbsp;
                    {formatWithCommas(
                      Number(
                        Helper.roundTo2(history?.after?.totalAccessoriesCost),
                      ),
                    )}
                  </Col>
                </Row>
              )}
            <Row className="row-cols-auto align-items-center justify-content-between my-1">
              <Col className="text-label font-weight-600 font-size-20">
                {currency}&nbsp;
                {formatWithCommas(
                  Number(
                    Helper.roundTo2(
                      history.installationCostRequested
                        ? history?.before?.grandTotal
                        : history?.before.equipmentCost,
                    ),
                  ),
                )}
              </Col>
              <Col className="text-advance-description font-weight-400 font-size-16">
                {history?.status === EstimationStatus.APPROVED ||
                history?.status === EstimationStatus.APPROVED_WITH_CHANGES ? (
                  "Total"
                ) : (
                  <>
                    {history?.installationCostRequested ? `Approximate` : ``}
                    &nbsp;Total
                  </>
                )}
              </Col>
              <Col className="text-label font-weight-600 font-size-20">
                {currency}&nbsp;
                {formatWithCommas(
                  Number(Helper.roundTo2(history?.after.grandTotal)),
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  const PreviousVersionRow = ({
    history,
    currency,
    findIcon,
    findName,
  }: HistoryVersionRowProps) => (
    <Row
      key={history.version}
      className="bg-background-10 px-1 py-3 rounded-2 mx-0 mt-4"
    >
      <Col>
        <Row className="align-items-center mb-2">
          <Col className="col-auto pe-0 text-label font-weight-500 font-size-20">
            Version {history.version}
          </Col>
          <Col className="text-secondary font-weight-400 font-size-10">
            {moment(history?.after?.lastUpdated).format("hh:mm a DD/MM/YYYY")}
          </Col>
        </Row>
        <Row className="align-items-center justify-content-between mx-0">
          <Col className="col-auto status-background text-color-6 py-1 px-3 rounded-5 font-weight-400 font-size-12">
            System Suggestion
          </Col>
          {history?.status === EstimationStatus.REJECTED ? (
            <Col
              className={`col-auto ${getStatusColor(
                EstimationStatus.REJECTED,
              )} py-1 px-3 rounded-5 font-weight-400 font-size-12 `}
            >
              Rejected by Admin
            </Col>
          ) : (
            <Col
              className={`col-auto ${getStatusColor(
                history.status as EstimationStatus,
              )} py-1 px-3 rounded-5 font-weight-400 font-size-12`}
            >
              {history.status === EstimationStatus.PENDING ? (
                `Submitting`
              ) : (
                <>
                  {getStatusNameUser(history?.status as EstimationStatus)}
                  &nbsp;by&nbsp;
                  {history?.status === EstimationStatus.APPROVED_WITH_CHANGES ||
                  history?.status === EstimationStatus.APPROVED ? (
                    `Admin`
                  ) : (
                    <>{isUserView ? `You` : user}</>
                  )}
                </>
              )}
            </Col>
          )}
        </Row>
        {history?.status !== EstimationStatus.REJECTED &&
          history?.userComment && (
            <Row className="my-3 mx-0">
              <Col>
                <AppBanner
                  content={
                    <Row>
                      <Col className="font-weight-400 font-size-12 text-color-7">
                        <span className="text-color-8">User Note:</span>
                        &nbsp;
                        {history?.userComment}
                      </Col>
                    </Row>
                  }
                  bannerVariant="lightBlueTwo"
                  className="text-start p-3 rounded-2"
                />
              </Col>
            </Row>
          )}
        {(history?.status === EstimationStatus.REJECTED ||
          history?.status === EstimationStatus.APPROVED_WITH_CHANGES ||
          history?.status === EstimationStatus.APPROVED) &&
          history?.adminComment && (
            <Row className="my-3 mx-0">
              <Col>
                <AppBanner
                  content={
                    <Row>
                      <Col className="font-weight-400 font-size-12 text-color-7">
                        <span className="text-color-8">Admin Note:</span>
                        &nbsp;
                        {history?.adminComment}
                      </Col>
                    </Row>
                  }
                  bannerVariant="lightBlueTwo"
                  className="text-start p-3 rounded-2"
                />
              </Col>
            </Row>
          )}
        {history?.estimationType !==
          ESolarSolutionType.WATER_HEATING_SYSTEM && (
          <>
            <Row className="mt-3">
              <Col className="text-secondary font-weight-500 font-size-13">
                Solar Power Equipments
              </Col>
            </Row>
            <Row>
              <Col>
                {history?.before?.products.length >
                history?.after?.products.length
                  ? history?.before?.products
                      .filter((i) => solarCategories.includes(i.category))
                      .map((i) => {
                        if (!i.id) return null;
                        const userProduct = findUserSelectedProduct(
                          history.version,
                          i.category,
                          false,
                        );
                        if (
                          i.category ===
                            EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS &&
                          !i.isSelected &&
                          !userProduct.isSelected
                        ) {
                          return null;
                        }
                        return (
                          <Row key={i.id} className="gx-3 my-2">
                            <Col>
                              {i.id &&
                              (i.category !==
                                EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS ||
                                i.isSelected) ? (
                                <SolarHistoryCard
                                  icon={findIcon(i.category)}
                                  name={findName(history.version, i.id)}
                                  count={i.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-12"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                            <Col>
                              {userProduct.id && userProduct.isSelected ? (
                                <SolarHistoryCard
                                  icon={userProduct.icon}
                                  name={userProduct.name}
                                  count={userProduct.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-11"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                          </Row>
                        );
                      })
                  : history?.after?.products
                      .filter((i) => solarCategories.includes(i.category))
                      .map((i) => {
                        if (!i.id) return null;
                        const userProduct = findUserSelectedProduct(
                          history.version,
                          i.category,
                          true,
                        );
                        if (
                          i.category ===
                            EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS &&
                          !i.isSelected &&
                          !userProduct.isSelected
                        ) {
                          return null;
                        }
                        return (
                          <Row key={i.id} className="gx-3 my-2">
                            <Col>
                              {userProduct.id && userProduct.isSelected ? (
                                <SolarHistoryCard
                                  icon={userProduct.icon}
                                  name={userProduct.name}
                                  count={userProduct.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-11"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                            <Col>
                              {i.id &&
                              (i.category !==
                                EViewSolarEstimationSolarProductCategory.CHARGE_CONTROLLERS ||
                                i.isSelected) ? (
                                <SolarHistoryCard
                                  icon={findIcon(i.category)}
                                  name={findName(history.version, i.id)}
                                  count={i.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-12"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                          </Row>
                        );
                      })}
              </Col>
            </Row>
          </>
        )}
        {(history?.estimationType === ESolarSolutionType.BOTH ||
          history?.estimationType ===
            ESolarSolutionType.WATER_HEATING_SYSTEM) && (
          <>
            <Row className="mt-2">
              <Col className="text-secondary font-weight-500 font-size-13">
                Water Heater Equipments
              </Col>
            </Row>
            <Row>
              <Col>
                {history?.before?.products.length >
                history?.after?.products.length
                  ? history?.before?.products
                      .filter((i) => waterCategories.includes(i.category))
                      .map((i) => {
                        if (!i.id) return null;
                        const userProduct = findUserSelectedProduct(
                          history.version,
                          i.category,
                          false,
                        );
                        return (
                          <Row key={i.id} className="gx-3 my-2">
                            <Col>
                              {i.id ? (
                                <SolarHistoryCard
                                  icon={findIcon(i.category)}
                                  name={findName(history.version, i.id)}
                                  count={i.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-12"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                            <Col>
                              {userProduct.id && userProduct.isSelected ? (
                                <SolarHistoryCard
                                  icon={userProduct.icon}
                                  name={userProduct.name}
                                  count={userProduct.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-11"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                          </Row>
                        );
                      })
                  : history?.after?.products
                      .filter((i) => waterCategories.includes(i.category))
                      .map((i) => {
                        if (!i.id) return null;
                        const userProduct = findUserSelectedProduct(
                          history.version,
                          i.category,
                          true,
                        );
                        return (
                          <Row key={i.id} className="gx-3 my-2">
                            <Col>
                              {userProduct.id && userProduct.isSelected ? (
                                <SolarHistoryCard
                                  icon={userProduct.icon}
                                  name={userProduct.name}
                                  count={userProduct.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-11"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                            <Col>
                              {i.id ? (
                                <SolarHistoryCard
                                  icon={findIcon(i.category)}
                                  name={findName(history.version, i.id)}
                                  count={i.count}
                                  bgColor={
                                    userProduct.id !== i.id ||
                                    userProduct.count !== i.count
                                      ? "bg-background-12"
                                      : ""
                                  }
                                />
                              ) : (
                                <div className="text-center text-label d-flex align-items-center justify-content-center h-100">
                                  -
                                </div>
                              )}
                            </Col>
                          </Row>
                        );
                      })}
              </Col>
            </Row>
          </>
        )}
        <Row className="mt-3">
          <Col>
            {!!history?.before?.equipmentCost &&
              !!history?.after?.equipmentCost && (
                <Row className="row-cols-auto align-items-center justify-content-between my-1">
                  <Col className="text-label font-weight-500 font-size-16">
                    {currency}&nbsp;
                    {formatWithCommas(
                      Number(Helper.roundTo2(history?.before?.equipmentCost)),
                    )}
                  </Col>
                  <Col className="text-advance-description font-weight-400 font-size-14">
                    Components Cost
                  </Col>
                  <Col className="text-label font-weight-500 font-size-16">
                    {currency}&nbsp;
                    {formatWithCommas(
                      Number(Helper.roundTo2(history?.after?.equipmentCost)),
                    )}
                  </Col>
                </Row>
              )}
            {!!history?.after?.totalLaborCost &&
              history.installationCostRequested && (
                <Row className="row-cols-auto align-items-center justify-content-between my-1">
                  <Col
                    xs={4}
                    className="text-start text-label font-weight-500 font-size-16"
                  >
                    {history?.before?.totalLaborCost ? (
                      <>
                        {currency}&nbsp;
                        {formatWithCommas(
                          Number(
                            Helper.roundTo2(history?.before?.totalLaborCost),
                          ),
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </Col>
                  <Col
                    xs={4}
                    className="text-center text-advance-description font-weight-400 font-size-14"
                  >
                    Approximate Installation Cost
                  </Col>
                  <Col
                    xs={4}
                    className="text-label font-weight-500 font-size-16 text-end"
                  >
                    {currency}&nbsp;
                    {formatWithCommas(
                      Number(Helper.roundTo2(history?.after?.totalLaborCost)),
                    )}
                  </Col>
                </Row>
              )}
            {!!history?.after?.totalAccessoriesCost &&
              history.installationCostRequested && (
                <Row className="row-cols-auto align-items-center justify-content-between my-1">
                  <Col
                    xs={4}
                    className="text-start text-label font-weight-500 font-size-16"
                  >
                    {history?.before?.totalAccessoriesCost ? (
                      <>
                        {currency}&nbsp;
                        {formatWithCommas(
                          Number(
                            Helper.roundTo2(
                              history?.before?.totalAccessoriesCost,
                            ),
                          ),
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </Col>
                  <Col
                    xs={4}
                    className="text-center text-advance-description font-weight-400 font-size-14"
                  >
                    Approximate Accessories Cost
                  </Col>
                  <Col
                    xs={4}
                    className="text-end text-label font-weight-500 font-size-16"
                  >
                    {currency}&nbsp;
                    {formatWithCommas(
                      Number(
                        Helper.roundTo2(history?.after?.totalAccessoriesCost),
                      ),
                    )}
                  </Col>
                </Row>
              )}
            <Row className="row-cols-auto align-items-center justify-content-between my-1">
              <Col className="text-label font-weight-600 font-size-20">
                {currency}&nbsp;
                {formatWithCommas(
                  Number(
                    Helper.roundTo2(
                      history.installationCostRequested
                        ? history?.before?.grandTotal
                        : history?.before?.equipmentCost,
                    ),
                  ),
                )}
              </Col>
              <Col className="text-advance-description font-weight-400 font-size-16">
                {history?.installationCostRequested ? `Approximate` : ``}
                &nbsp;Total
              </Col>
              <Col className="text-label font-weight-600 font-size-20">
                {currency}&nbsp;
                {formatWithCommas(
                  Number(
                    Helper.roundTo2(
                      history.installationCostRequested
                        ? history?.after?.grandTotal
                        : history?.after?.equipmentCost,
                    ),
                  ),
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );

  return (
    <ModalContainer
      title={`#${estimationNo} Update History`}
      {...rest}
      show={show}
      size="modal-xl"
      isButtonsVisible={false}
    >
      <>
        {updateHistory.length > 0 &&
          [...updateHistory]
            .reverse()
            .map((history: ISolarEstimationUpdateHistoryView) =>
              updateHistory[updateHistory.length - 1].version ===
                history.version &&
              (updateHistory[updateHistory.length - 1].status ===
                EstimationStatus.PURCHASED ||
                updateHistory[updateHistory.length - 1].status ===
                  EstimationStatus.APPROVED ||
                updateHistory[updateHistory.length - 1].status ===
                  EstimationStatus.APPROVED_WITH_CHANGES ||
                updateHistory[updateHistory.length - 1].status ===
                  EstimationStatus.MODIFIED ||
                updateHistory[updateHistory.length - 1].status ===
                  EstimationStatus.REJECTED) ? (
                <CurrentVersionRow
                  key={history.version}
                  history={history}
                  currency={currency}
                  findIcon={findIcon}
                  findName={findName}
                />
              ) : (
                <PreviousVersionRow
                  key={history.version}
                  history={history}
                  currency={currency}
                  findIcon={findIcon}
                  findName={findName}
                />
              ),
            )}
        <SpinnerModal show={isFetching} />
      </>
    </ModalContainer>
  );
};

export default UpdateHistoryModal;
